import React, { useState } from "react";
import { Cloud, Server, Lock, User, ShieldCheck, Cpu, ArrowDown } from "lucide-react";
import { track } from "../../lib/analytics";

const OPTIONS = [
  {
    id: "hosted",
    name: "Hosted",
    icon: Cloud,
    sensitivity: "Lower sensitivity",
    desc: "Run by norse3 as a managed service, subject to your validation and data requirements.",
    residency: "Data path defined and validated with you; suited to commercial, lower-sensitivity use.",
  },
  {
    id: "private",
    name: "Private cloud",
    icon: Server,
    sensitivity: "Medium sensitivity",
    desc: "Deployed within your own cloud tenancy for tighter data-residency control.",
    residency: "Runs inside your tenancy so data residency and network boundaries stay under your control.",
  },
  {
    id: "customer",
    name: "Customer-controlled",
    icon: Lock,
    sensitivity: "Higher sensitivity",
    desc: "Operated inside your controlled environment where regulation or sensitivity requires it.",
    residency: "Deployed into an environment you operate; specific architecture requires technical assessment.",
  },
];

export default function DeploymentExplorer() {
  const [active, setActive] = useState("private");
  const cur = OPTIONS.find((o) => o.id === active);

  return (
    <div data-testid="defence-deployment">
      <div className="grid sm:grid-cols-3 gap-2.5">
        {OPTIONS.map((o) => {
          const Icon = o.icon;
          const on = o.id === active;
          return (
            <button
              key={o.id}
              onClick={() => { setActive(o.id); track("defence_deployment_selected", { option: o.id }); }}
              data-testid={`deployment-${o.id}`}
              className={`rounded-xl border px-4 py-4 text-left transition-colors ${
                on ? "border-primary/40 bg-primary/[0.07]" : "border-white/10 bg-navy-900/60 hover:border-white/20"
              }`}
            >
              <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${on ? "bg-primary/15 text-primary" : "bg-white/[0.04] text-muted-foreground"}`}><Icon className="h-5 w-5" /></span>
              <p className={`mt-3 text-sm font-semibold ${on ? "text-foreground" : "text-muted-foreground"}`}>{o.name}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{o.sensitivity}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-4 grid lg:grid-cols-[1fr_320px] gap-4">
        <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
          {[
            { icon: User, label: "User / system", sub: "Operators, analysts, connected workflows", tag: null },
            { icon: ShieldCheck, label: "norse3 control layer", sub: cur.name + " deployment", tag: "CONTROL" },
            { icon: Cpu, label: "Approved AI services", sub: "The models and agents you choose to deploy", tag: "AI" },
          ].map((row, i, arr) => {
            const Icon = row.icon;
            const mid = row.tag === "CONTROL";
            return (
              <div key={row.label}>
                <div className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 ${mid ? "border-primary/40 bg-primary/[0.06]" : "border-white/10 bg-navy-800/60"}`}>
                  <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${mid ? "bg-primary/15 text-primary" : "bg-white/[0.04] text-muted-foreground"}`}><Icon className="h-5 w-5" /></span>
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-foreground">{row.label}</span>
                    <span className="block text-xs text-muted-foreground">{row.sub}</span>
                  </span>
                  {row.tag && <span className={`rounded-full border px-2 py-0.5 text-[0.6rem] font-semibold ${mid ? "border-primary/30 text-primary" : "border-white/15 text-muted-foreground"}`}>{row.tag}</span>}
                </div>
                {i < arr.length - 1 && <div className="flex justify-center py-0.5"><ArrowDown className="h-3.5 w-3.5 text-muted-foreground/40" /></div>}
              </div>
            );
          })}
        </div>
        <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">{cur.name}</p>
          <p className="mt-3 text-sm text-foreground/90 leading-relaxed">{cur.desc}</p>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{cur.residency}</p>
        </div>
      </div>

      <p className="mt-4 rounded-xl border border-signal-amber/25 bg-signal-amber/[0.04] px-5 py-4 text-sm text-signal-amber/90">
        Specific security classifications, network architecture, accreditation requirements and data-handling controls require technical assessment. Deployment requirements differ according to the sensitivity of the environment.
      </p>
    </div>
  );
}
